<template>
  <gg-card>
    <gg-card-header class="pb-0">
      <v-row>
        <v-col md="6" cols="12" class="d-flex align-center">
          <gg-btn
            icon
            class="mr-2"
            @click="$emit('collapse')"
          >
            <v-icon color="secondary">{{ collapsed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </gg-btn>
          <v-icon size="32px" color="primary" class="mr-2">mdi-nas</v-icon>
          {{ groupName }}
          <v-chip
            class="ml-3"
            small
          >
            {{ items.length }} {{ items.length === 1 ? 'website' : 'websites' }}
          </v-chip>
          <v-chip
            class="ml-3"
            small
            :color="isActive ? 'primary' : 'warning'"
          >
            {{ isActive ? 'Active' : 'Inactive' }}
          </v-chip>
        </v-col>
        <v-col md="6" cols="12" class="d-flex flex-sm-row flex-column justify-end align-start gap-sm align-md-center">
          <slot name="header-actions" />
          <gg-btn
            secondary
            class="ml-3 text-none"
            @click="navigateToSubAccount({ id: serviceId })"
          >
            Manage Plan
          </gg-btn>
        </v-col>
      </v-row>
    </gg-card-header>
    <gg-card-text class="px-0 py-0">
      <v-expand-transition>
        <v-data-table
          v-show="!collapsed"
          class="rounded-xl"
          :loading="loading"
          :headers="table.headers"
          :items="items"
          item-key="virtualId"
          sort-by="domain"
          :footer-props="{ itemsPerPageOptions: pagination }"
        >
          <template v-slot:item.domain="{ item }">
            <div class="d-flex items-center">
              <v-icon size="14" color="primary" class="mr-2">mdi-web</v-icon>
              <router-link :to="item" custom v-slot="{ navigate }">
                <button
                  @click="navigateToSubAccount({ id: item.serviceId, user: item.user })"
                  role="link"
                  aria-label="Manage"
                  class="ggs-item-title primary--text">
                  {{ item.domain }}
                </button>
              </router-link>
              <router-link
                v-if="item.sharedaccount || item.sharedaccess"
                :to="{ name: 'profile_access' }"
                class="pl-1"
              >
                <v-icon small class="mb-1 ml-1">mdi-account-multiple</v-icon>
              </router-link>
              <v-icon
                color="grey darken-3"
                size="12"
                class="ml-2"
              >mdi-open-in-new</v-icon>
            </div>
          </template>
          <template v-slot:item.domaintype="{ item }">
            {{ DOMAIN_TYPE_NAMES[item.domaintype] }}
          </template>
          <template v-slot:item.flag="{ item }">
            <v-icon class="mr-1 caption">{{ iconFlag(item.flag) }}</v-icon>
            {{ item.flag }}
          </template>
          <template v-slot:item.status="{ item }">
            <div>
              <v-icon small class="pb-1" :color="statusColor(item.status)">mdi-circle</v-icon>
              <span class="pl-1">{{ item.status }}</span>
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <gg-btn
              class="mr-1"
              @click="navigateToSubAccount({ id: item.serviceId, user: item.user })"
            >
              Manage
            </gg-btn>
            <gg-btn
              v-if="item.type === SERVICE_TYPES.CPANEL || item.type === SERVICE_TYPES.RESELLER"
              class="mr-1"
              :to="createEmailUrl(item)"
              secondary
            >
              Email
            </gg-btn>
            <gg-btn
              v-if="item.wp_insid"
              class="mr-1"
              :to="createWpUrl(item)"
              secondary
            >
              WordPress
            </gg-btn>
            <gg-btn
              v-if="item.type === SERVICE_TYPES.CPANEL || item.type === SERVICE_TYPES.RESELLER"
              secondary
              class="mr-1 text-none"
              @click="launch_cpanel(item.serviceId, item.username)"
            >
              cPanel
            </gg-btn>
            <gg-btn
              v-if="item.type === SERVICE_TYPES.RESELLER"
              secondary
              class="mr-1"
              @click="launch_whm(item.serviceId)"
            >
              WHM
            </gg-btn>
          </template>
        </v-data-table>
      </v-expand-transition>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  statusColor,
  iconFlag,
  launch_cpanel,
  launch_whm,
} from '@/helpers/helpers'
import { SERVICE_TYPES } from '@/consts/service'
import { DOMAIN_TYPE_NAMES } from '@/consts/domains'

export default {
  name: 'WebsitesGroupTable',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    groupName: {
      type: String,
      required: true,
    },
    serviceId: {
      type: [String, Number],
      required: true,
    },
    isActive: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      table: {
        headers: [
          {
            text: 'Website URL',
            value: 'domain',
          },
          {
            text: 'Type',
            value: 'domaintype',
          },
          {
            text: 'Location',
            value: 'flag',
          },
          {
            text: 'Status',
            value: 'status',
          },
          {
            text: 'Actions',
            value: 'actions',
            align: 'end',
          }
        ],
      },
      SERVICE_TYPES,
      DOMAIN_TYPE_NAMES,
    }
  },
  computed: {
    ...mapGetters(['pagination']),
  },
  methods: {
    ...mapActions(['navigateToSubAccount']),
    statusColor,
    iconFlag,
    launch_cpanel,
    launch_whm,
    createEmailUrl (website) {
      let targetUrl = ''
      if (website.type === this.SERVICE_TYPES.CPANEL) {
        targetUrl = `/hosting/shared/${website.serviceId}/email-accounts`
      } else if (website.type === this.SERVICE_TYPES.RESELLER) {
        targetUrl = `/hosting/shared/${website.serviceId}/email-accounts?user=${website.username}`
      }
      return targetUrl
    },
    createWpUrl (website) {
      let targetUrl = ''
      if (!website.wp_insid) {
        return targetUrl
      }
      if (website.type === this.SERVICE_TYPES.CPANEL) {
        targetUrl = `/hosting/shared/${website.serviceId}/wordpress/install-and-manage`
      } else if (website.type === this.SERVICE_TYPES.RESELLER) {
        targetUrl = `/hosting/shared/${website.serviceId}/wordpress/install-and-manage?user=${website.username}`
      }
      return targetUrl
    },
  },
}
</script>
